import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import {
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { reviewFeedFilters } from "../lib/reviewFeed";
import { useReviewReturn } from "./transcripts/useReviewReturn";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  parseTranscriptTimelineSearch,
  transcriptListReturnSearch,
  type TranscriptTimelineSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
  type SearchPatch,
} from "../lib/typedRouter";
import { transcriptAudioUrl } from "../lib/transcriptAudio";
import { useListQuery } from "../lib/useListQuery";
import { markerDisplayById as buildMarkerDisplay } from "./TranscriptAudio";
import { AuthoringLeaveGuard } from "./AuthoringLeaveGuard";
import { TranscriptTimelineView } from "./TranscriptTimelineView";
import {
  expandedRow,
  firstRoutedRow,
  nextRow,
  resultsRailRows,
} from "./transcripts/resultsRailRows";
import { labelLeaveBlocks } from "./transcripts/labelInspectorSupport";
import { useLabelInspector } from "./transcripts/useLabelInspector";
import { TranscriptRerunAction } from "./transcripts/TranscriptRerunControl";
import { useTranscriptIdentity } from "./transcripts/useTranscriptIdentity";
import { useAudioTransport } from "./transcripts/useAudioTransport";
import { useTranscriptRerun } from "./transcripts/useTranscriptRerun";
import { useTranscriptNotes } from "./transcripts/useTranscriptNotes";
import { useTranscriptVariables } from "./transcripts/useTranscriptVariables";
import { useVariableSuppliers } from "./transcripts/useVariableSuppliers";
import {
  useTraceNodeNames,
  useTraceSpanPagination,
} from "./transcripts/useTraceNodeNames";

type TimelineSearchState = TranscriptTimelineSearch & WorkingScopeSearch;

export function TranscriptTimeline() {
  const { id } = useParams({
    from: "/authenticated/billing-gate/app-shell/transcripts/$id",
  });
  const navigate = useTypedNavigate<TimelineSearchState>();
  const search = useTypedSearch(
    withWorkingScopeSearch(parseTranscriptTimelineSearch),
  );
  const transport = useAudioTransport();
  const rerun = useTranscriptRerun(id);
  const noteProps = useTranscriptNotes(id);
  const feed = isShowcaseMode ? undefined : search.feed;
  const returnToReview = useReviewReturn(feed);
  const pinnedMarkerId = feed ? reviewFeedFilters(feed).marker_id : undefined;

  const transcript = useQuery({
    queryKey: queryKeys.transcripts.detail(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/transcripts/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const transcriptHasAudio = Boolean(transcript.data?.audio_uri);
  const audioSrc = transcriptHasAudio ? transcriptAudioUrl(id) : null;
  const transcriptAgentId = transcript.data?.agent_id;
  const identity = useTranscriptIdentity(transcript.data);

  const variablesOpen = search.panel === "variables";
  const transcriptVariables = useTranscriptVariables(
    id,
    transcript.data?.source === "simulated" && variablesOpen,
  );
  const variableSuppliers = useVariableSuppliers(
    {
      personaId: transcriptVariables.data?.persona_id ?? null,
      scenarioId: transcriptVariables.data?.scenario_id ?? null,
    },
    !isShowcaseMode && variablesOpen,
  );

  const peaks = useQuery({
    queryKey: queryKeys.transcripts.audioPeaks(id),
    enabled: transcriptHasAudio,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const result = await apiClient.GET("/v1/transcripts/{id}/audio/peaks", {
        params: { path: { id } },
      });
      if (result.error && result.response.status !== 404) {
        throw new ApiRequestError(result.error);
      }
      return result.data ?? null;
    },
  });

  const markers = useListQuery("/v1/markers", {
    queryKey: queryKeys.markers.list({ agent_id: transcriptAgentId }),
    enabled: transcriptAgentId != null,
    params: { agent_id: transcriptAgentId },
  });

  const traces = useListQuery("/v1/traces", {
    queryKey: queryKeys.traces.list({ transcript_id: id }),
    params: { transcript_id: id },
  });

  const answers = useListQuery("/v1/answers", {
    params: { transcript_id: id },
    queryKey: queryKeys.answers.list({ transcript_id: id }),
  });
  const markerDisplayById = useMemo(
    () => buildMarkerDisplay(markers.items),
    [markers.items],
  );
  const utterances = transcript.data?.content;
  const rows = useMemo(
    () =>
      resultsRailRows(
        answers.items,
        markerDisplayById,
        utterances ?? [],
        pinnedMarkerId,
      ),
    [answers.items, markerDisplayById, utterances, pinnedMarkerId],
  );
  const label = isShowcaseMode
    ? undefined
    : (search.label ??
      (pinnedMarkerId
        ? rows.find((row) => row.answer.marker_id === pinnedMarkerId)?.answer.id
        : feed
          ? firstRoutedRow(rows)?.answer.id
          : undefined));
  const expandedId = expandedRow(rows, label, answers.items)?.answer.id;
  const traceNodeNames = useTraceNodeNames(traces.items);
  const canRerun = transcript.data?.status === "ready";
  const reevaluateOne = (markerId: string) => {
    rerun.mutate([markerId]);
  };
  useTraceSpanPagination(search.span_id, traceNodeNames, traces);

  function patch(
    next: SearchPatch<TimelineSearchState>,
    options: { ignoreBlocker?: boolean; replace?: boolean } = {},
  ): void {
    void navigate({
      ...options,
      search: mergeSearch<TimelineSearchState>(next),
    });
  }
  const labeling = useLabelInspector({
    agentId: transcriptAgentId,
    answers,
    feed,
    label,
    markerId: isShowcaseMode ? undefined : search.marker,
    markers,
    onClose: () => {
      patch({ label: undefined, marker: undefined }, { replace: true });
    },
    onMarkerChange: (marker) => {
      patch({ marker });
    },
    onOpenNext: (answer, afterSave) => {
      void navigate({
        ignoreBlocker: afterSave,
        params: { id: answer.transcript_id },
        search: (current) => ({
          ...current,
          label: answer.id,
          marker: undefined,
          panel: undefined,
          span_id: undefined,
        }),
        to: "/transcripts/$id",
      });
    },
    onReturnToReview: returnToReview,
    onSaved: (created) => {
      if (search.label === "new") {
        patch(
          { label: undefined, marker: undefined },
          { ignoreBlocker: true, replace: true },
        );
        return;
      }
      const next = nextRow(rows, expandedId);
      patch(
        {
          label: next?.answer.id ?? expandedId ?? created.id,
          marker: undefined,
        },
        { ignoreBlocker: true },
      );
    },
    transcriptId: id,
  });

  return (
    <>
      <AuthoringLeaveGuard
        isDirty={Boolean(labeling.isDirty)}
        isPending={labeling.isSaving}
        subject="Label"
        shouldBlockFn={labelLeaveBlocks}
      />
      <TranscriptTimelineView
        activeSpanId={search.span_id}
        {...identity}
        answers={answers}
        audioIsPending={peaks.isPending}
        audioPeaks={peaks.data?.peaks ?? null}
        audioSrc={audioSrc}
        currentMs={transport.currentMs}
        isPlaying={transport.isPlaying}
        evaluateAction={
          transcript.data?.status === "ready" ? (
            <TranscriptRerunAction
              markers={markers}
              rerun={rerun}
              rows={rows}
            />
          ) : null
        }
        expandedId={expandedId}
        feed={feed != null}
        pinnedMarkerId={pinnedMarkerId}
        labeling={labeling}
        markerDisplayById={markerDisplayById}
        {...noteProps}
        panel={search.panel}
        onAddQuestion={() => {
          patch({ label: "new", marker: undefined, panel: undefined });
        }}
        onBack={() => {
          if (feed) {
            returnToReview();
          } else {
            void navigate({
              search: transcriptListReturnSearch,
              to: "/transcripts",
            });
          }
        }}
        onClosePanel={() => {
          patch({ panel: undefined, span_id: undefined }, { replace: true });
        }}
        onCloseQuestion={() => {
          patch({ label: undefined, marker: undefined }, { replace: true });
        }}
        onExpandAnswer={(answerId) => {
          patch({ label: answerId, marker: undefined });
        }}
        onOpenPanel={(panel) => {
          patch({ panel });
        }}
        onTraceNodeRequest={(spanId) => {
          patch({ panel: "traces", span_id: spanId });
        }}
        onPlayingChange={transport.onPlayingChange}
        onReevaluate={canRerun ? reevaluateOne : undefined}
        onSeekRequest={transport.requestSeek}
        onTimeUpdate={transport.onTimeUpdate}
        seekRequest={transport.seekRequest}
        traceError={traces.errorMessage ?? null}
        traces={traces.items}
        tracesIsPending={traces.isPending}
        transcript={transcript.data ?? null}
        transcriptError={transcript.isError ? transcript.error.message : null}
        transcriptIsPending={transcript.isPending}
        variables={transcriptVariables.data ?? null}
        variableSuppliers={variableSuppliers}
        variablesError={
          transcriptVariables.isError ? transcriptVariables.error.message : null
        }
        variablesIsPending={transcriptVariables.isPending}
      />
    </>
  );
}
